import { useEffect } from 'react';
import swal from 'sweetalert2';
import { getOrderStatus } from '../api/billingAPI';

const STORAGE_KEY = 'ib_billing_orderKey';

/**
 * Resolves a payment the browser never got to confirm (Phase 8).
 *
 * ==================== THE CASE THIS EXISTS FOR ====================
 *
 * A customer pays on their phone's UPI app and the tab is killed while
 * they are out of the browser. Or the network drops between Razorpay and
 * our verifyPayment call. Their money has gone; nothing on screen says so.
 *
 * The webhook completes the activation server-side regardless - that is
 * the design, and it works whether or not this component ever runs. What
 * this adds is TELLING THE CUSTOMER, so they do not conclude the purchase
 * failed and pay a second time.
 *
 * checkoutModal writes the orderKey to sessionStorage BEFORE opening
 * Razorpay. This reads it on the next mount and asks the server what
 * actually happened.
 *
 * ==================== WHY IT POLLS BRIEFLY, NOT FOREVER ====================
 *
 * The webhook usually lands in seconds, but "usually" is not "always" -
 * so this checks a handful of times over about half a minute and then
 * stops. If it is still not resolved, the reconcile job will finish the
 * job within 15 minutes and the customer sees it on their next visit. A
 * component that polls indefinitely is a component that hammers the API
 * from an abandoned tab.
 *
 * Renders nothing. It is a side effect with a UI, not a screen.
 */

const POLL_INTERVAL_MS = 4000;
const MAX_ATTEMPTS     = 8;      // ~32 seconds

const PaymentStatus = (props) => {
    const { onResolved } = props;

    useEffect(() => {
        const orderKey = sessionStorage.getItem(STORAGE_KEY);
        if (!orderKey) return;

        let cancelled = false;
        let attempts  = 0;
        let timer     = null;

        const finish = (clear) => {
            if (clear) sessionStorage.removeItem(STORAGE_KEY);
            if (timer) clearTimeout(timer);
        };

        const check = async () => {
            if (cancelled) return;
            attempts += 1;

            try {
                const status = await getOrderStatus(orderKey);

                if (status.status === 'PAID') {
                    finish(true);
                    swal.fire({
                        icon              : 'success',
                        titleText         : 'Payment confirmed',
                        html              : `<p>Your subscription is active.</p>${status.invoiceNo ? `<p class="kt-font-sm">Invoice ${status.invoiceNo}</p>` : ''}`,
                        buttonsStyling    : false,
                        confirmButtonClass: 'btn btn-brand',
                    });
                    if (onResolved) onResolved(status);
                    return;
                }

                if (status.status === 'FAILED' || status.status === 'CANCELLED' || status.status === 'EXPIRED') {
                    finish(true);
                    swal.fire({
                        icon              : 'error',
                        titleText         : 'Payment not completed',
                        text              : 'That payment did not go through. Nothing has been charged.',
                        buttonsStyling    : false,
                        confirmButtonClass: 'btn btn-brand',
                    });
                    if (onResolved) onResolved(status);
                    return;
                }

                // Still AWAITING_PAYMENT. Keep the key so the next page
                // load can try again.
                if (attempts < MAX_ATTEMPTS) {
                    timer = setTimeout(check, POLL_INTERVAL_MS);
                } else {
                    finish(false);
                }

            } catch (err) {
                // An order we cannot read is one we should stop asking
                // about - it belongs to another tenant, or never existed.
                // Silent on purpose: this runs on page load and an error
                // popup about a payment the user may have abandoned is
                // noise.
                finish(true);
            }
        };

        check();

        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, []);

    return null;
};

export default PaymentStatus;
